import { apiClient } from '@/lib/api-client';

/**
 * Fetches the AG Grid license key from the API endpoint.
 *
 * Calls the endpoint `GET /keys/ag_grid_key` which returns an object mapping
 * license key names to their values.
 *
 * @returns Promise resolving to the AG Grid license key string
 *
 * @throws {ApiError} If the API request fails (401, 403, 422, etc.)
 * - 401: Unauthorized (session expired or not authenticated)
 * - 403: Forbidden (insufficient permissions)
 * - 422: Validation error
 * @throws {Error} If the response is null or the `ag_grid_key` property is missing
 *
 * @example
 * ```typescript
 * const licenseKey = await fetchAgGridLicense();
 * LicenseManager.setLicenseKey(licenseKey);
 * ```
 */
export async function fetchAgGridLicense(): Promise<string> {
  const response = await apiClient.get<{ [key: string]: string }>('/keys/ag_grid_key');

  if (!response) {
    throw new Error('No license key returned from API');
  }

  // Read the license key by its explicit property name for deterministic extraction
  const licenseKey = response.ag_grid_key;

  if (!licenseKey) {
    throw new Error('License key not found in API response');
  }

  return licenseKey;
}
